import type { CuttingStrategy } from './CuttingStrategy';
import type { IMaterial } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';

export class FirstFitStrategy implements CuttingStrategy {
    name = 'First Fit';

    optimize(material: IMaterial, parts: IMaterial[]): ICutResult {
        // Sort parts by height (largest first)
        const sortedParts = [...parts].sort((a, b) => b.height - a.height);
        const usedMaterials: IMaterial[] = [];
        const remainingParts = [...sortedParts];

        while (remainingParts.length > 0) {
            const currentMaterial: IMaterial = { ...material, id: `material-${usedMaterials.length + 1}`, children: [] as IMaterial[] };

            // Simple row-based placement
            let currentY = 0;
            let rowHeight = 0;
            let currentX = 0;

            for (let i = 0; i < remainingParts.length; i++) {
                const part = remainingParts[i];

                // If we can fit the part in the current row
                if (currentX + part.width <= material.width) {
                    const placedPart: IMaterial = {
                        ...part,
                        x: currentX,
                        y: currentY
                    };

                    currentMaterial.children!.push(placedPart);
                    currentX += part.width;
                    rowHeight = Math.max(rowHeight, part.height);

                    // Remove from remaining parts
                    remainingParts.splice(i, 1);
                    i--; // Adjust index after removal
                }
            }

            // Move to next row
            currentY += rowHeight;

            // If we've placed at least one part, or if we can't fit any more parts
            if (currentMaterial.children!.length > 0) {
                usedMaterials.push(currentMaterial);
            } else {
                // If we couldn't place any parts, break to avoid infinite loop
                break;
            }

            // If we've reached the height limit, start a new material
            if (currentY >= material.height && remainingParts.length > 0) {
                continue;
            }
        }

        return {
            usedMaterials,
            wastePercentage: this.calculateWaste(material, usedMaterials),
            strategyName: this.name
        };
    }

    private calculateWaste(originalMaterial: IMaterial, usedMaterials: IMaterial[]): number {
        const totalArea = originalMaterial.width * originalMaterial.height * usedMaterials.length;
        let usedArea = 0;

        usedMaterials.forEach(material => {
            if (material.children) {
                material.children.forEach(part => {
                    usedArea += part.width * part.height;
                });
            }
        });

        return ((totalArea - usedArea) / totalArea) * 100;
    }
}
